<template lang="pug">
    form(ref="formData" @submit.prevent='process()' :class="{'-dark':isDark,'-hasError':hasError}" novalidate)
        slot(name="header")
        slot(name="error")
            div(v-if="formErrors['general']" class="errors text-center text-white text-sm py-3 px-1 mb-3 bg-danger rounded") {{ formErrors['general'] }}
        slot(:errors="formErrors" :hasError="hasError")
        slot(name="buttons" ref="buttons" :hasError="hasError")
</template>
<script>
/**
 * A basic Form Wrapper to facilitate Error Handling and Input Validation.
 * Takes in a Save callback to call once all inputs are determined to be valid.
 */
export default {
    /**
     * Allows us to converse with all of our children, even the grand kids.
     * Children have to use inject() to gain access to these.
     */
    provide() {
        const $form = {
            register: this.registerField,
            unregister: this.unregisterField,
            addError: this.addError, // Function to add an error to the stack
            clearError: this.clearError, // Function to remove an error from the stack
        }
        // This is required for Reactive for nested data
        // Creates a Reactive Getter
        Object.defineProperty($form, 'errors', {
            enumerable: true,
            get: () => this.formErrors,
        })
        return {
            $form,
        }
    },
    props: {
        isDark: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            fields: {},
            formErrors: {},
        }
    },
    computed: {
        // Allows us to style css changes based on if any element in the form has an error
        hasError() {
            return Object.keys(this.formErrors).length > 0
        },
    },
    methods: {
        /**
         * Each input registers itself to the Form so we can reliably loop through them.
         * @param {String} inputName is the Name of the input
         * @param {Function} validate is the Validate() method for this input
         */
        registerField(inputName, validate) {
            // Map Field Name to Validate Method
            this.fields[inputName] = validate
        },
        /**
         * on Destroy we remove the input reference
         * @param {String} inputName is the Name of the input
         */
        unregisterField(inputName) {
            // Remove Field by Name
            delete this.fields[inputName]
        },
        /**
         * Remove an error from the error stack
         * @param {String} inputName - the name of the input we wish to remove from the error stack
         */
        clearError(inputName) {
            // $delete removes the error in a Reactive way
            if (typeof this.formErrors[inputName] !== 'undefined') {
                this.$delete(this.formErrors, inputName)
            }
        },
        /**
         * Adds an error to our error stack
         * @param {String} inputName - Should match an Input or be 'general' to display ontop
         * @param {String} error
         */
        addError(inputName, error) {
            // $set adds an error in a Reactive way
            this.$set(this.formErrors, inputName, error)
        },
        process() {
            // clear our errors to start fresh
            this.formErrors = {}

            // Validates all fields registered to this form
            for (const inputName in this.fields) {
                // eslint-disable-next-line no-prototype-builtins
                if (this.fields.hasOwnProperty(inputName)) {
                    // Execute Validate Methods local to each field which will fill this.formErrors
                    this.fields[inputName]()
                }
            }

            // Early out if anythin is invalid
            if (Object.keys(this.formErrors).length > 0) return

            // Tell the parent all is well
            this.$emit('submit')
        },
    },
}
</script>
<style lang="stylus" scoped>
.errors
    >>> .link
        color theme('colors.white')
        text-decoration underline
</style>
